import { MemberDto } from '@/services/api'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import { HowToReg, PersonOff, Warning } from '@mui/icons-material'
import {
  Alert,
  Box,
  Collapse,
  Fade,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { MemberSituationDTO } from '@services/dtos'
import StorageService from '@services/storage'
import { doGetMemberSituation } from '@state/reducers/billingReducer'
import {
  setFloatingMemberSituationExpanded,
  setSelectedMemberSituation,
} from '@state/reducers/orderReducer'
import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import MemberAutocomplete from './MemberAutocomplete'
import { useIntl } from 'react-intl'

const FloatingMemberSituationSelect: React.FC = () => {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const containerRef = useRef<HTMLDivElement | null>(null)

  const { selectedMemberSituation, isExpanded } = useAppSelector((state) => ({
    selectedMemberSituation: state.order.selectedMemberSituation,
    isExpanded: state.order.isFloatingMemberSituationExpanded,
  }))
  const [selectedMember, setSelectedMember] = useState<MemberDto | null>(
    selectedMemberSituation?.member || null,
  )
  const [memberSituation, setMemberSituation] =
    useState<MemberSituationDTO | null>(
      selectedMemberSituation?.situation || null,
    )
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const cachedData = StorageService.getMemberSituationCache()
    if (cachedData && cachedData.member) {
      setSelectedMember(cachedData.member)
    }
  }, [])

  useEffect(() => {
    if (selectedMember) {
      setIsLoading(true)
      dispatch(doGetMemberSituation(selectedMember.id))
        .unwrap()
        .then((situation) => {
          setMemberSituation(situation)
          if (situation) {
            const newMemberSituation = {
              member: selectedMember,
              situation,
            }
            handleMemberSituationChange(newMemberSituation)
            StorageService.setMemberSituationCache(selectedMember, situation)
          } else {
            handleMemberSituationChange(null)
          }
        })
        .catch(() => {
          setMemberSituation(null)
          handleMemberSituationChange(null)
        })
        .finally(() => setIsLoading(false))
    } else {
      setMemberSituation(null)
      handleMemberSituationChange(null)
    }
  }, [selectedMember, dispatch])

  const handleMemberSituationChange = useCallback(
    (
      memberSituation: {
        member: MemberDto
        situation: MemberSituationDTO
      } | null,
    ) => {
      dispatch(setSelectedMemberSituation(memberSituation))
    },
    [dispatch],
  )

  const handleMemberSelect = (member: MemberDto | null) => {
    setSelectedMember(member)
  }

  const toggleExpanded = () => {
    dispatch(setFloatingMemberSituationExpanded(!isExpanded))
  }

  useEffect(() => {
    if (!isExpanded) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isInsideContainer =
        containerRef.current && containerRef.current.contains(target)

      if (isInsideContainer) {
        return
      }

      const poppers = document.querySelectorAll('.member-autocomplete-popper')
      const clickedPopper = Array.from(poppers).some((popper) =>
        popper.contains(target),
      )

      if (!clickedPopper) {
        dispatch(setFloatingMemberSituationExpanded(false))
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExpanded, dispatch])

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        mx: 2,
      }}
    >
      <Collapse
        orientation="vertical"
        in={isExpanded}
        timeout={200}
        sx={{
          position: 'fixed',
          bottom: '5.2%',
          left: '1%',
          '& .MuiCollapse-wrapper': {
            transition:
              'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important',
          },
          '& .MuiCollapse-wrapperInner': {
            transition:
              'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important',
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            p: 2,
            mr: 0,
            minWidth: 280,
            border: '1px solid #2434',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {intl.formatMessage({ id: 'member-selected' })}
            </Typography>
            <IconButton
              onClick={toggleExpanded}
              size="small"
              sx={{
                width: 24,
                height: 24,
                color: '#666',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  color: '#333',
                },
              }}
            >
              <X size={16} />
            </IconButton>
          </Box>
          <MemberAutocomplete
            label={intl.formatMessage({ id: 'member' })}
            value={selectedMember}
            onChange={handleMemberSelect}
          />
          {selectedMember && !memberSituation && !isLoading && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
              {intl.formatMessage({ id: 'member-situation-missing' })}
            </Alert>
          )}
        </Box>
      </Collapse>

      <Fade in={!isLoading} timeout={200}>
        <Tooltip title="Sélection du médecin" placement="top-start">
          <IconButton
            onClick={toggleExpanded}
            sx={{
              display: 'inline-flex',
              gap: 1,
              color: 'white',
              borderRadius: '100%',
            }}
            className={`
            ${
              selectedMember && memberSituation
                ? '!text-green-400'
                : '!text-orange-400'
            }`}
          >
            {selectedMember && memberSituation && <HowToReg />}
            {!selectedMember ? (
              <PersonOff className="animate-pulse" />
            ) : !memberSituation && selectedMember ? (
              <Warning className="animate-pulse" />
            ) : null}
            <Typography
              variant="body1"
              fontWeight="medium"
              color="inherit"
              className={!memberSituation ? '!animate-pulse' : ''}
            >
              {selectedMember
                ? `${selectedMember.lastName} ${selectedMember.firstName}`
                : intl.formatMessage({ id: 'no-member-selected' })}
            </Typography>
          </IconButton>
        </Tooltip>
      </Fade>
    </Box>
  )
}

export default FloatingMemberSituationSelect
